import { Box, Typography } from "@mui/material";
import type { GridColDef } from "@mui/x-data-grid";
import type { DateTime } from "luxon";
import { type FC, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import EmploymentTypeSelector from "../../components/employmentTypeSelector/EmploymentTypeSelector";
import { TEAM_SECTIONS } from "../../components/team/config";
import { SystemSwitch } from "../../components/UIToolkit/Clickable/SystemSwitch";
import { StaffAutoComplete } from "../../components/UIToolkit/Inputs/StaffAutoComplete";
import type { SystemAutoCompleteOption } from "../../components/UIToolkit/Inputs/SystemAutoComplete/SystemAutoComplete";
import { DateRangeSelector } from "../../components/UIToolkit/molecules/DateRangeSelector";
import { JobSelector } from "../../components/UIToolkit/molecules/JobSelector";
import { RoleChips } from "../../components/UIToolkit/molecules/RoleChips";
import { SystemDataGrid } from "../../components/UIToolkit/Tables/SystemDataGrid";
import { useAuth } from "../../contexts/AuthProvider";
import { useAnalytics } from "../../contexts/analytics/hooks";
import { FEATURE_FLAGS } from "../../contexts/FeatureFlagsProvider";
import { useHospitalData } from "../../contexts/HospitalDataProvider";
import { useDebouncedValue } from "../../hooks/useDebouncedValue";
import { useFormatPersonName } from "../../hooks/useNameFormat";
import { useFeatureFlag } from "../../hooks/useOpenFeatureFlag";
import { leakageReportTableSortingKey, localStorageService } from "../../services/LocalStorageService";
import type { LeakageReportRow, Nurse, NurseFullRole, NurseRole } from "../../utils/models";
import type { MultiSortOption, SortingState } from "../../utils/multiColumnSorting";
import { inverseRolesMap, nurseRoleDisplayName, rolesMap } from "../../utils/utilFunctions";
import { CountCell, EmploymentTypeCell, HoursCell, JobCell, LastShiftCell } from "./LeakageReportCells";
import { getDefaultLeakageRange, isValidWholeWeekEnd, minEndDate, parseRangeDate, sectionsToEmploymentTypes } from "./leakageReport.utils";
import { useGetLeakageReport } from "./useLeakageReport";

const FILTER_DEBOUNCE_MS = 750;

/** Sort rows by their jobs' display names (the array can't be compared by the default sorter). */
const compareJobs = (a: LeakageReportRow, b: LeakageReportRow): 1 | 0 | -1 => {
	const aName = a.job.map((j) => j.displayName).join(", ");
	const bName = b.job.map((j) => j.displayName).join(", ");
	return aName < bName ? -1 : aName > bName ? 1 : 0;
};

const LeakageReport: FC = () => {
	const { t } = useTranslation();
	const { chosenDepartment, hospitalData } = useHospitalData();
	const formatPersonName = useFormatPersonName();
	const { track } = useAnalytics();
	const { userInfo } = useAuth();
	const navigate = useNavigate();
	const jobManagementEnabled = useFeatureFlag(FEATURE_FLAGS.JobManagementEnabled);

	const userId = userInfo?.id ?? "";
	const departmentId = chosenDepartment.department_id;
	const allJobIds = useMemo(() => (chosenDepartment.jobs ?? []).map((j) => j.id), [chosenDepartment.jobs]);
	const allSectionTitles = useMemo(() => TEAM_SECTIONS.map((s) => s.title), []);
	// Persisted filters (role / job / contract type / include-PTO only) — read once on mount.
	const storedFilters = useMemo(() => (userId ? localStorageService.getLeakageReportFilters(departmentId, userId) : null), [userId, departmentId]);

	const defaultRange = useMemo(() => getDefaultLeakageRange(chosenDepartment.week_start_day), [chosenDepartment.week_start_day]);
	const [searchParams, setSearchParams] = useSearchParams();
	const [startDate, setStartDate] = useState<DateTime>(() => parseRangeDate(searchParams.get("startDate")) ?? defaultRange.startDate);
	const [endDate, setEndDate] = useState<DateTime>(() => parseRangeDate(searchParams.get("endDate")) ?? defaultRange.endDate);
	const [selectedRoles, setSelectedRoles] = useState<string[]>(() => storedFilters?.roles ?? []);
	const [selectedJobs, setSelectedJobs] = useState<string[]>(() => storedFilters?.jobs ?? allJobIds);
	const [selectedSections, setSelectedSections] = useState<ReadonlySet<string>>(() => new Set(storedFilters?.sections ?? allSectionTitles));
	const [selectedNurse, setSelectedNurse] = useState<SystemAutoCompleteOption<Nurse> | null>(null);
	const [includePto, setIncludePto] = useState<boolean>(() => storedFilters?.includePto ?? true);

	// RoleChips needs short NurseRole codes; we store the full NurseFullRole values, so map back.
	const initiallySelectedRoles = useMemo<(NurseRole | "all")[] | undefined>(
		() => (storedFilters?.roles?.length ? storedFilters.roles.map((r) => inverseRolesMap[r as NurseFullRole]) : undefined),
		[storedFilters],
	);

	useEffect(() => {
		track("leakage_report_viewed", { department_id: chosenDepartment.department_id });
	}, [track, chosenDepartment.department_id]);

	useEffect(() => {
		// Persist only the role / job / contract-type / include-PTO filters (not the date range or
		// staff search) so they carry across visits.
		if (!userId) return;
		localStorageService.setLeakageReportFilters(
			departmentId,
			{ roles: selectedRoles, jobs: selectedJobs, sections: [...selectedSections], includePto },
			userId,
		);
	}, [userId, departmentId, selectedRoles, selectedJobs, selectedSections, includePto]);

	useEffect(() => {
		// Keep the URL in sync with the selected range (replace: don't add a history entry per change).
		setSearchParams(
			(prev) => {
				const next = new URLSearchParams(prev);
				next.set("startDate", startDate.toISODate() ?? "");
				next.set("endDate", endDate.toISODate() ?? "");
				return next;
			},
			{ replace: true },
		);
	}, [startDate, endDate, setSearchParams]);

	const trackFilterChange = useCallback(
		(filter: string) => track("leakage_report_filters_changed", { department_id: chosenDepartment.department_id, filter }),
		[track, chosenDepartment.department_id],
	);

	const handleRowClick = useCallback(
		(row: LeakageReportRow) => {
			track("leakage_report_nurse_clicked", { department_id: chosenDepartment.department_id, nurse_id: row.nurseId });
			navigate(`/${chosenDepartment.department_id}/nurse/${row.nurseId}`, { state: { navigationSrc: window.location.pathname } });
		},
		[navigate, track, chosenDepartment.department_id],
	);

	const employmentTypes = useMemo(() => sectionsToEmploymentTypes(selectedSections), [selectedSections]);
	const debouncedRoles = useDebouncedValue(selectedRoles, FILTER_DEBOUNCE_MS);
	const debouncedJobs = useDebouncedValue(selectedJobs, FILTER_DEBOUNCE_MS);
	const debouncedEmploymentTypes = useDebouncedValue(employmentTypes, FILTER_DEBOUNCE_MS);
	const debouncedNurseId = useDebouncedValue(selectedNurse?.value.id, FILTER_DEBOUNCE_MS);

	const { data, isFetching, isError } = useGetLeakageReport({
		startDate,
		endDate,
		roles: debouncedRoles.length ? debouncedRoles : undefined,
		jobs: jobManagementEnabled && debouncedJobs.length && debouncedJobs.length < allJobIds.length ? debouncedJobs : undefined,
		employmentTypes: debouncedEmploymentTypes,
		nurseId: debouncedNurseId,
	});

	const nurseOptions = useMemo<SystemAutoCompleteOption<Nurse>[]>(
		() => hospitalData.nurses.map((n) => ({ label: formatPersonName(n) || `#${n.id}`, value: n })),
		[hospitalData.nurses, formatPersonName],
	);

	// The PTO toggle is purely presentational: it selects which precomputed average the hour
	// columns display and sort on (with- vs. without-PTO), so flipping it never refetches.
	const scheduledField: keyof LeakageReportRow = includePto ? "scheduledAvgHoursWithPto" : "scheduledAvgHours";
	const actualField: keyof LeakageReportRow = includePto ? "actualAvgHoursWithPto" : "actualAvgHours";

	const columns = useMemo<GridColDef<LeakageReportRow>[]>(() => {
		const cols: GridColDef<LeakageReportRow>[] = [
			{ field: "fullName", headerName: t("leakage_report.column.full_name"), flex: 1.4, minWidth: 180 },
			{
				field: "role",
				headerName: t("leakage_report.column.role"),
				flex: 0.8,
				minWidth: 110,
				renderCell: ({ row }) => nurseRoleDisplayName[row.role as NurseRole] ?? row.role,
			},
		];
		if (jobManagementEnabled) {
			cols.push({ field: "job", headerName: t("leakage_report.column.job"), flex: 1.2, minWidth: 140, renderCell: ({ row }) => <JobCell value={row.job} /> });
		}
		cols.push(
			{
				field: "employmentType",
				headerName: t("leakage_report.column.employment_type"),
				flex: 1,
				minWidth: 130,
				renderCell: ({ row }) => <EmploymentTypeCell value={row.employmentType} />,
			},
			{
				field: scheduledField,
				headerName: t("leakage_report.column.scheduled_avg_hours"),
				type: "number",
				flex: 1,
				minWidth: 150,
				renderCell: ({ row }) => <HoursCell value={row[scheduledField]} />,
			},
			{
				field: actualField,
				headerName: t("leakage_report.column.actual_avg_hours"),
				type: "number",
				flex: 1,
				minWidth: 150,
				renderCell: ({ row }) => <HoursCell value={row[actualField]} />,
			},
		);
		if (includePto) {
			cols.push({
				field: "ptoHours",
				headerName: t("leakage_report.column.pto_hours"),
				type: "number",
				flex: 0.9,
				minWidth: 110,
				renderCell: ({ row }) => <HoursCell value={row.ptoHours} />,
			});
		}
		cols.push(
			{
				field: "weekendsWorked",
				headerName: t("leakage_report.column.weekends_worked"),
				type: "number",
				flex: 1,
				minWidth: 150,
				renderCell: ({ row }) => <CountCell value={row.weekendsWorked} />,
			},
			{
				field: "lastShiftWorked",
				headerName: t("leakage_report.column.last_shift_worked"),
				flex: 1,
				minWidth: 150,
				renderCell: ({ row }) => <LastShiftCell value={row.lastShiftWorked} />,
			},
		);
		return cols;
	}, [t, jobManagementEnabled, includePto, scheduledField, actualField]);

	const sortingOptions = useMemo<MultiSortOption<LeakageReportRow>[]>(() => {
		const options: MultiSortOption<LeakageReportRow>[] = [
			{ label: t("leakage_report.column.full_name"), sortColumns: ["fullName"] },
			{ label: t("leakage_report.column.role"), sortColumns: ["role"] },
			...(jobManagementEnabled
				? [{ label: t("leakage_report.column.job"), sortColumns: ["job" as keyof LeakageReportRow], customSortFunction: compareJobs }]
				: []),
			{ label: t("leakage_report.column.employment_type"), sortColumns: ["employmentType"] },
			{ label: t("leakage_report.column.scheduled_avg_hours"), sortColumns: [scheduledField] },
			{ label: t("leakage_report.column.actual_avg_hours"), sortColumns: [actualField] },
			...(includePto ? [{ label: t("leakage_report.column.pto_hours"), sortColumns: ["ptoHours" as keyof LeakageReportRow] }] : []),
			{ label: t("leakage_report.column.weekends_worked"), sortColumns: ["weekendsWorked"] },
			{ label: t("leakage_report.column.last_shift_worked"), sortColumns: ["lastShiftWorked"] },
		];
		return options;
	}, [t, jobManagementEnabled, includePto, scheduledField, actualField]);

	const defaultSortState = useMemo<SortingState<LeakageReportRow>[]>(
		() => [{ sortColumns: ["fullName"], sortOrder: "asc", label: t("leakage_report.column.full_name") }],
		[t],
	);

	const filtersNode = (
		<>
			<DateRangeSelector
				startDate={startDate}
				endDate={endDate}
				endMinDate={minEndDate(startDate)}
				// Only allow end dates that complete whole pay-period weeks relative to the start.
				shouldDisabledEndDate={(date) => !isValidWholeWeekEnd(startDate, date)}
				// Let the start be picked freely; onStartDateChange realigns the end below.
				allowStartAfterEnd={true}
				removeBackground={true}
				removeBorder={true}
				onStartDateChange={(date) => {
					setStartDate(date);
					// Realign the end if the new start makes it a partial week (fall back to one week).
					if (!isValidWholeWeekEnd(date, endDate)) setEndDate(minEndDate(date));
					trackFilterChange("date_range");
				}}
				onEndDateChange={(date) => {
					setEndDate(date);
					trackFilterChange("date_range");
				}}
			/>
			<StaffAutoComplete
				nurses={nurseOptions}
				value={selectedNurse}
				width={220}
				onChange={(_, value) => {
					setSelectedNurse(value);
					trackFilterChange("nurse");
				}}
			/>
			<RoleChips
				initiallySelected={initiallySelectedRoles}
				onSelectedRoleFiltersChanged={(roles) => {
					// RoleChips emits short NurseRole codes; nurse.role stores the full NurseFullRole
					// value, so map through rolesMap before sending to the backend filter.
					setSelectedRoles(roles.filter((r): r is NurseRole => r !== "all").map((r) => rolesMap[r]));
					trackFilterChange("roles");
				}}
			/>
			{jobManagementEnabled && (
				<JobSelector
					jobs={chosenDepartment.jobs ?? []}
					selectedJobIds={selectedJobs}
					onSelectedJobsChanged={(jobs) => {
						setSelectedJobs(jobs);
						trackFilterChange("jobs");
					}}
					variant="dropdown"
					label={t("leakage_report.column.job")}
					formControlSx={{ width: 220 }}
				/>
			)}
			<EmploymentTypeSelector
				selectSx={{ width: 150 }}
				initialSelectedSections={[...selectedSections]}
				onSectionsChange={(sections) => {
					setSelectedSections(sections);
					trackFilterChange("employment_type");
				}}
			/>
		</>
	);

	return (
		<Box data-testid="leakage-report-page" sx={{ p: 3, display: "flex", flexDirection: "column", gap: 2, height: "100%", width: "100%" }}>
			<Typography variant="h5" sx={{ fontWeight: 600, color: "primary.dark" }}>
				{t("leakage_report.title")}
			</Typography>

			<Box sx={{ flex: 1, minHeight: 0, width: "100%", display: "flex" }}>
				<SystemDataGrid
					rows={data?.rows ?? []}
					columns={columns}
					getRowId={(row) => row.nurseId}
					loading={isFetching}
					error={isError}
					sortingOptions={sortingOptions}
					defaultSortState={defaultSortState}
					localStorageKey={leakageReportTableSortingKey}
					leadingToolbarNodes={filtersNode}
					trailingToolbarNodes={
						<SystemSwitch
							checked={includePto}
							onChange={(e) => {
								setIncludePto(e.target.checked);
								trackFilterChange("include_pto");
							}}
							label={t("leakage_report.include_pto")}
							dataTestId="leakage-report-include-pto"
						/>
					}
					toolbarSxOverride={{ gap: 1.5, rowGap: 2 }}
					dataGridProps={{ onRowClick: ({ row }) => handleRowClick(row) }}
					sx={{ "& .MuiDataGrid-row": { cursor: "pointer" } }}
					dataTestId="leakage-report"
				/>
			</Box>
		</Box>
	);
};

export default LeakageReport;
